// @ts-nocheck
// Clerk components throw TypeScript errors, but work fine
import { useRouter } from '@/hooks/useRouter'
import { SignedIn, SignedOut, UserButton, ClerkLoading } from '@clerk/clerk-react'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { LogIn, LoaderIcon, CircleHelp, Settings, Plus, BookOpen } from 'lucide-react'
import { useHall } from '@/contexts/HallProvider'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Link } from 'react-router-dom'
import { ModeToggle } from './ui/theme-toggle'
import { useAuth } from '@/contexts/AuthProvider'
import { useContactModal } from '@/contexts/ContactModalProvider'

export const NavBar = () => {
  const { hall, setRunTour } = useHall()
  const { pathname } = useRouter()
  const inHall = pathname.includes('hall')
  const { currentUser, currentOrg, appUserLoaded, setActiveOrg, setShowOrgModal } = useAuth()
  const { openContact } = useContactModal()

  return (
    <div className="w-full flex items-center px-4 py-2 bg-gray-100 dark:bg-background shadow-lg z-50 border-b border-white overflow-x-hidden">
      {/* LEFT: logo (never shrinks) */}
      <Link to="/" className="shrink-0">
        <div className="flex items-center">
          <img
            src="/db8ai-logo.webp"
            alt="DB8AI.com Logo"
            className="h-8 rounded-full border border-2 border-yellow-300"
          />
          <span className="font-bold text-lg pl-2">DB8AI.com</span>
        </div>
      </Link>

      {/* MIDDLE: topic (takes remaining space, can shrink; no horizontal overflow) */}
      <div className="flex-1 flex justify-center px-2 min-w-0">
        {hall && (
          <h1
            className="
              font-bold text-center
              text-xs md:text-sm lg:text-base
              overflow-hidden text-ellipsis whitespace-nowrap
              max-w-full
            "
            title={hall.topic}
          >
            {hall.topic}
          </h1>
        )}
      </div>

      {/* RIGHT: controls (never shrinks below content) */}
      <div className="shrink-0 flex items-center gap-4">
        <div className="flex items-center gap-4">
          {/* Learning & Toolkit (signed-in) */}
          <SignedIn>
            <Link to="/learning-toolkit">
              <Button
                variant="outline"
                className="min-h-8"
                disabled={!appUserLoaded}
                title={!appUserLoaded ? 'Loading account…' : 'Learning & Toolkit'}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Learning &amp; Toolkit <BookOpen size="16px" />
                </span>
              </Button>
            </Link>
          </SignedIn>

          {/* New Debate (homepage only) */}
          {appUserLoaded && currentOrg && pathname === '/' && (
            <Link to={'/create-debate'}>
              <Button
                variant="marketing"
                className="bg-violet-700 min-h-8 hover:bg-violet-800 dark:bg-violet-600 dark:hover:bg-violet-700"
              >
                <span className="relative z-10 flex items-center gap-3">
                  New Debate <Plus size="17px" />
                </span>
              </Button>
            </Link>
          )}

          {/* Org switcher */}
          {currentUser?.adminOfOrgs.length > 0 && (
            <div className="relative">
              <DropdownMenu>
                <div className="flex items-center border border-input rounded-md overflow-hidden bg-background text-sm shadow-sm">
                  <DropdownMenuTrigger asChild disabled={hall}>
                    <Button
                      variant="ghost"
                      className="capitalize flex-1 justify-start px-3 py-2 h-full rounded-none"
                      style={{ zIndex: 1 }}
                    >
                      {currentOrg?.name || 'Select Org'}
                    </Button>
                  </DropdownMenuTrigger>
                  {currentUser?.id === currentOrg?.founderId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowOrgModal(true)
                      }}
                      className="px-2 h-full hover:bg-muted border-l border-border"
                      style={{ zIndex: 2 }}
                    >
                      <Settings size={16} />
                    </button>
                  )}
                </div>

                <DropdownMenuContent className="z-50 min-w-[8rem] rounded-md border p-1 shadow-md bg-gray-200 dark:bg-gray-800 -ml-[-40px]">
                  {currentUser.adminOfOrgs.map((org) => (
                    <DropdownMenuItem
                      key={org.id}
                      onSelect={() => setActiveOrg(org.id)}
                      className={`cursor-pointer hover:bg-green-200 ${
                        currentOrg?.id === org.id ? 'border border-green-500' : ''
                      }`}
                    >
                      {org.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Feedback / Support (signed-in only) */}
          <SignedIn>
            <Button
              variant="outline"
              className="text-xs"
              onClick={() => openContact()}
              disabled={!appUserLoaded}
              title={!appUserLoaded ? 'Loading account…' : 'Contact us'}
            >
              Contact Us
            </Button>
          </SignedIn>

          {/* Auth */}
          <div className="flex items-center gap-2">
            {/* Help menu (between Feedback/Support and Theme/Profile) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="cursor-pointer h-full">
                  <CircleHelp size="20px" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="z-50 min-w-[8rem] rounded-md border p-1 shadow-md bg-gray-200 dark:bg-gray-800">
                <a target="_blank" href="https://www.speechanddebate.org/">
                  <DropdownMenuItem className="text-center cursor-pointer w-full flex justify-center hover:bg-accent">
                    NSDA
                  </DropdownMenuItem>
                </a>
                <a target="_blank" href="https://www.speechwire.com/">
                  <DropdownMenuItem className="text-center cursor-pointer w-full flex justify-center hover:bg-accent">
                    Speechwire
                  </DropdownMenuItem>
                </a>
                <a target="_blank" href="https://www.tabroom.com/index/index.mhtml">
                  <DropdownMenuItem className="text-center cursor-pointer w-full flex justify-center hover:bg-accent">
                    Tabroom
                  </DropdownMenuItem>
                </a>
                {inHall && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => setRunTour(true)}
                      className="text-center cursor-pointer w-full flex justify-center hover:bg-accent"
                    >
                      Start app tour
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Dark mode toggle (right side, next to profile) */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <ModeToggle />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Toggle Dark Mode</p>
              </TooltipContent>
            </Tooltip>

            <SignedIn>
              <Button variant="outline" size="icon">
                <UserButton />
              </Button>
            </SignedIn>
            <SignedOut>
              <a href="/sign-in" aria-label="Sign in">
                <LogIn className="cursor-pointer" />
              </a>
            </SignedOut>
            <ClerkLoading>
              <LoaderIcon />
            </ClerkLoading>
          </div>
        </div>
      </div>
    </div>
  )
}
